import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  GraduationCap, Calendar, Users, FileCheck, AlertTriangle,
  UserCheck, Building, Clock, Check, Info
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export function StudentLetManagement({ propertyId, jurisdiction }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (propertyId) {
      loadConfig();
    }
  }, [propertyId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/property-types/properties/${propertyId}/student-config`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );

      if (response.ok) {
        setConfig(await response.json());
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/property-types/properties/${propertyId}/student-config`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(config)
        }
      );

      if (response.ok) {
        toast.success('Configuration saved');
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HMO Trigger Warning */}
      {config?.triggers_hmo_requirements && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">HMO Requirements May Apply</p>
              <p className="text-sm text-amber-700 mt-1">
                Based on the number of tenants and property configuration, this student let 
                may trigger HMO licensing requirements in {jurisdiction || 'your jurisdiction'}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Letting Mode */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-primary" />
          Letting Mode
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label 
            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
              config?.letting_mode === 'group' 
                ? 'border-primary bg-primary/5' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="letting_mode"
              value="group"
              checked={config?.letting_mode === 'group'}
              onChange={(e) => updateConfig('letting_mode', e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold">Group Letting</p>
                <p className="text-sm text-slate-500">
                  One tenancy for the whole group
                </p>
              </div>
            </div>
          </label>

          <label 
            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
              config?.letting_mode === 'room_by_room' 
                ? 'border-primary bg-primary/5' 
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <input
              type="radio"
              name="letting_mode"
              value="room_by_room"
              checked={config?.letting_mode === 'room_by_room'}
              onChange={(e) => updateConfig('letting_mode', e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold">Room-by-Room</p>
                <p className="text-sm text-slate-500">
                  Individual tenancies per room
                </p>
              </div>
            </div>
          </label>
        </div>
      </Card>

      {/* Academic Year Settings */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Academic Year Settings
        </h3>

        <div className="space-y-4">
          <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">Align with Academic Year</p>
              <p className="text-sm text-slate-500">Tenancy dates follow university term</p>
            </div>
            <input
              type="checkbox"
              checked={config?.academic_year_aligned}
              onChange={(e) => updateConfig('academic_year_aligned', e.target.checked)}
              className="w-5 h-5 rounded border-slate-300"
            />
          </label>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Typical Start</label>
              <select
                value={config?.typical_start_date || 'September'}
                onChange={(e) => updateConfig('typical_start_date', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'
                ].map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Typical End</label>
              <select
                value={config?.typical_end_date || 'June'}
                onChange={(e) => updateConfig('typical_end_date', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'
                ].map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Requirements */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-primary" />
          Requirements
        </h3>

        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <UserCheck className="w-5 h-5 text-slate-600" />
              <div>
                <p className="font-medium">Guarantor Required</p>
                <p className="text-sm text-slate-500">Require UK-based guarantor</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={config?.guarantor_required}
              onChange={(e) => updateConfig('guarantor_required', e.target.checked)}
              className="w-5 h-5 rounded border-slate-300"
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-slate-600" />
              <div>
                <p className="font-medium">Student Verification</p>
                <p className="text-sm text-slate-500">Verify student status</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={config?.student_verification_required}
              onChange={(e) => updateConfig('student_verification_required', e.target.checked)}
              className="w-5 h-5 rounded border-slate-300"
            />
          </label>

          {config?.letting_mode === 'group' && (
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="font-medium">Group Applications</p>
                  <p className="text-sm text-slate-500">Allow groups to apply together</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={config?.group_application_enabled}
                onChange={(e) => updateConfig('group_application_enabled', e.target.checked)}
                className="w-5 h-5 rounded border-slate-300"
              />
            </label>
          )}
        </div>
      </Card>

      {/* University Affiliation */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-primary" />
          University Affiliation
        </h3>

        <div>
          <label className="block text-sm font-medium mb-1">Nearby University (Optional)</label>
          <input
            type="text"
            value={config?.university_affiliation || ''}
            onChange={(e) => updateConfig('university_affiliation', e.target.value)}
            placeholder="e.g., University of Edinburgh"
            className="w-full px-3 py-2 border rounded-lg"
          />
          <p className="text-sm text-slate-500 mt-1">
            This helps market to students from specific universities
          </p>
        </div>

        {config?.letting_mode === 'group' && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Max Group Size</label>
            <input
              type="number"
              min="2"
              max="20"
              value={config?.max_group_size || ''}
              onChange={(e) => updateConfig('max_group_size', parseInt(e.target.value) || null)}
              placeholder="e.g., 6"
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        )}
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveConfig} disabled={saving}>
          {saving ? (
            <><Clock className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Check className="w-4 h-4 mr-2" /> Save Configuration</>
          )}
        </Button>
      </div>
    </div>
  );
}

export default StudentLetManagement;
